/**
 * Unified storage abstraction for auth tokens.
 *
 * - Admin auth uses Zustand with localStorage persist (backwards compatible)
 * - Portal auth uses namespaced localStorage keys per client
 */

const TOKEN_NAMESPACE = 'touchpoint_portal';

// Portal storage key generators (kept consistent with existing implementation)
export function getPortalTokenKey(clientId: string): string {
  return `${TOKEN_NAMESPACE}_token_${clientId}`;
}

export function getPortalRoleKey(clientId: string): string {
  return `${TOKEN_NAMESPACE}_role_${clientId}`;
}

export function getPortalClientNameKey(clientId: string): string {
  return `${TOKEN_NAMESPACE}_client_name_${clientId}`;
}

export function getPortalRefreshTokenKey(clientId: string): string {
  return `${TOKEN_NAMESPACE}_refresh_token_${clientId}`;
}

// Portal storage operations
export interface PortalAuthData {
  token: string;
  refreshToken?: string;
  role: string;
  clientName?: string;
}

export function getPortalAuth(clientId: string): PortalAuthData | null {
  try {
    const token = localStorage.getItem(getPortalTokenKey(clientId));
    const role = localStorage.getItem(getPortalRoleKey(clientId));

    if (!token || !role) {
      return null;
    }

    const clientName = localStorage.getItem(getPortalClientNameKey(clientId)) || undefined;
    const refreshToken = localStorage.getItem(getPortalRefreshTokenKey(clientId)) || undefined;

    return { token, refreshToken, role, clientName };
  } catch (error) {
    console.error('[storage] Failed to get portal auth:', error);
    return null;
  }
}

/**
 * Custom DOM event used to broadcast in-tab portal auth changes. The
 * native ``storage`` event only fires across tabs, not within the tab
 * that issued the write, so subscribers in the same tab (usePortalAuth)
 * would otherwise keep a stale ``isAuthenticated=true`` until a route
 * change forced a re-check.
 */
const PORTAL_AUTH_EVENT = 'touchpoint:portal-auth-change';

interface PortalAuthEventDetail {
  clientId: string;
}

function emitPortalAuthChange(clientId: string): void {
  try {
    window.dispatchEvent(
      new CustomEvent<PortalAuthEventDetail>(PORTAL_AUTH_EVENT, {
        detail: { clientId },
      })
    );
  } catch (error) {
    console.error('[storage] Failed to emit portal auth change:', error);
  }
}

export function setPortalAuth(clientId: string, data: PortalAuthData): void {
  try {
    localStorage.setItem(getPortalTokenKey(clientId), data.token);
    localStorage.setItem(getPortalRoleKey(clientId), data.role);
    if (data.clientName) {
      localStorage.setItem(getPortalClientNameKey(clientId), data.clientName);
    }
    if (data.refreshToken) {
      localStorage.setItem(getPortalRefreshTokenKey(clientId), data.refreshToken);
    }
    emitPortalAuthChange(clientId);
  } catch (error) {
    console.error('[storage] Failed to set portal auth:', error);
  }
}

export function clearPortalAuth(clientId: string): void {
  try {
    localStorage.removeItem(getPortalTokenKey(clientId));
    localStorage.removeItem(getPortalRoleKey(clientId));
    localStorage.removeItem(getPortalClientNameKey(clientId));
    localStorage.removeItem(getPortalRefreshTokenKey(clientId));
    emitPortalAuthChange(clientId);
  } catch (error) {
    console.error('[storage] Failed to clear portal auth:', error);
  }
}

/**
 * Check if portal auth exists for a client without loading all data.
 */
export function hasPortalAuth(clientId: string): boolean {
  try {
    const token = localStorage.getItem(getPortalTokenKey(clientId));
    const role = localStorage.getItem(getPortalRoleKey(clientId));
    return Boolean(token && role);
  } catch {
    return false;
  }
}

/**
 * Listen for portal auth changes -- both cross-tab (native ``storage``
 * event) and in-tab (``PORTAL_AUTH_EVENT`` CustomEvent emitted by
 * ``setPortalAuth`` / ``clearPortalAuth``). The in-tab channel is what
 * lets a 401 in portalClient flip ``isAuthenticated`` immediately
 * instead of waiting for a navigation.
 */
export function onPortalAuthChange(
  clientId: string,
  callback: (data: PortalAuthData | null) => void
): () => void {
  const tokenKey = getPortalTokenKey(clientId);

  const storageHandler = (event: StorageEvent) => {
    if (event.key === tokenKey) {
      callback(getPortalAuth(clientId));
    }
  };

  const inTabHandler = (event: Event) => {
    const detail = (event as CustomEvent<PortalAuthEventDetail>).detail;
    if (detail?.clientId === clientId) {
      callback(getPortalAuth(clientId));
    }
  };

  window.addEventListener('storage', storageHandler);
  window.addEventListener(PORTAL_AUTH_EVENT, inTabHandler);

  return () => {
    window.removeEventListener('storage', storageHandler);
    window.removeEventListener(PORTAL_AUTH_EVENT, inTabHandler);
  };
}
